import { useCallback, useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import {
	STATSIG_FULL_PAGE_SEARCH_LAYER,
	STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED,
	useExperimentConfig,
} from '@atlassian/search-experiment';

import { PrimaryProductKeys } from '../../../common/constants/products';
import { PRELOAD_SEARCH_PAGE_STATE } from '../../../common/constants/relay/preload-state';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { useAppContext } from '../../../common/ui/app-context';
import {
	getBootstrapVariables,
	getSearchVariables,
} from '../../../common/utils/relay/get-variables';
import { logException } from '../../../controllers/error-boundary/utils';
import { useBootstrapState } from '../../../controllers/store/bootstrap';
import { useSelectedInputFilters } from '../../../controllers/store/dropdown';
import { useSearchState } from '../../../controllers/store/search';

/**
 * This component exists purely to collect state for preloading the queries
 * from Quick Find. As Quick Find boostrap state is slighly different, we need to
 * map it to the Search Page bootstrap state. E.g. experimentConfig
 * Once we refactor to remove RSS, this component can be removed.
 */
export const FullPageSearchPreloadSubscriber = () => {
	const envProvider = useRelayEnvironmentProvider();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const search: SearchVariables = useSearchState(getSearchVariables);
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const bootstrap: BootstrapVariables = useBootstrapState(getBootstrapVariables);
	const [selectedInputFilters] = useSelectedInputFilters();
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	const userId = bootstrap.user?.id;
	const primaryProduct = bootstrap.primaryProduct;

	const onError = useCallback(
		(error: Error) => {
			logException(error, userId, primaryProduct);
		},
		[userId, primaryProduct],
	);

	const experimentLayers = useMemo(
		() => bootstrap.experimentLayers ?? [],
		[bootstrap.experimentLayers],
	);
	const sideBySideExperiment = useMemo(() => [], []); // No side-by-side experiments for preload

	const experimentConfig = useExperimentConfig({
		combinedExperimentLayer:
			primaryProduct === PrimaryProductKeys.Jira
				? STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED
				: undefined,
		experimentLayers,
		sideBySideExperiment,
		statsigLayer: fg('refactor_mutually_exclusive_search_ui_layers')
			? undefined
			: STATSIG_FULL_PAGE_SEARCH_LAYER,
		onError,
	});

	useEffect(() => {
		PRELOAD_SEARCH_PAGE_STATE.update({
			bootstrap: {
				...bootstrap,
				// This is required to ensure we have the right experiment config during preload
				experimentConfig,
			},
			search,
			selectedInputFilters,
			// It's most likely better to call useRelayEnvironmentProvider from the component using
			// the environment, rather than passing it like this. But given the existing preload
			// architecture, this is the least invasive way to achieve this for now.
			environment: envProvider.getEnvironment({}),
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
		});
	}, [
		bootstrap,
		search,
		selectedInputFilters,
		envProvider,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		experimentConfig,
	]);

	useEffect(() => {
		return () => {
			PRELOAD_SEARCH_PAGE_STATE.dispose();
		};
	}, []);
	return null;
};
